import { useCallback, useEffect, useMemo, useState } from 'react';

type Breakpoint =
  | 'phone'
  | 'tablet'
  | 'tablet-large'
  | 'desktop'
  | 'desktopLarge'
  | 'unknown';

const useBreakpoints = () => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('phone');

  // Memoize the CSS variable conversion to avoid recalculating on every resize
  const breakpointValues = useMemo(() => {
    if (typeof window === 'undefined') return null;

    const convertCssVariableToPx = (name: string): number => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
      if (value.endsWith('rem')) {
        const rem = parseFloat(value.replace('rem', ''));
        return (
          rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
        );
      }
      return parseInt(value, 10);
    };

    return {
      phone: convertCssVariableToPx('--breakpoint-phone'),
      tablet: convertCssVariableToPx('--breakpoint-tablet'),
      tabletLarge: convertCssVariableToPx('--breakpoint-tablet-large'),
      desktop: convertCssVariableToPx('--breakpoint-desktop'),
      desktopLarge: convertCssVariableToPx('--breakpoint-desktop-large'),
    };
  }, []);

  const determineBreakpoint = useCallback(
    (width: number): Breakpoint => {
      if (!breakpointValues) return 'unknown';

      const { tablet, tabletLarge, desktop, desktopLarge } = breakpointValues;

      if (width < tablet) return 'phone';
      if (width < tabletLarge) return 'tablet';
      if (width < desktop) return 'tablet-large';
      if (width < desktopLarge) return 'desktop';
      return 'desktopLarge';
    },
    [breakpointValues],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !breakpointValues) return;

    let timeoutId: NodeJS.Timeout;

    const update = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setBreakpoint(determineBreakpoint(window.innerWidth));
      }, 100); // Debounce resize events
    };

    update(); // initial

    window.addEventListener('resize', update);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', update);
      clearTimeout(timeoutId);
    };
  }, [breakpointValues, determineBreakpoint]);

  return {
    breakpoint,
    isPhone: breakpoint === 'phone',
    isTablet: breakpoint === 'tablet',
    isTabletLarge: breakpoint === 'tablet-large',
    isDesktop: breakpoint === 'desktop',
    isDesktopLarge: breakpoint === 'desktopLarge',
  };
};

export default useBreakpoints;
